import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { CallbackForm } from './CallbackForm';
import { useTranslation } from '../i18n';
interface CallbackModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export function CallbackModal({
  isOpen,
  onClose
}: CallbackModalProps) {
  const {
    lang
  } = useTranslation();
  const contentRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      // Reset modal scroll to top
      if (contentRef.current) {
        contentRef.current.scrollTop = 0;
      }
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  // Close on ESC
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);
  // Close on backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === backdropRef.current) {
      onClose();
    }
  };
  if (!isOpen) return null;
  return <div ref={backdropRef} onClick={handleBackdropClick} className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in" data-id="element-117">
      <div ref={contentRef} className="relative w-full sm:max-w-md max-h-[90vh] overflow-y-auto bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl animate-slide-up" data-id="element-118">
        {/* Close button */}
        <button onClick={onClose} className="absolute top-4 right-4 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-700 transition-colors" aria-label="Close" data-id="element-119">
          <X className="w-4 h-4" data-id="element-120" />
        </button>

        {/* Drag handle (mobile) */}
        <div className="flex justify-center pt-3 pb-1 sm:hidden" data-id="element-121">
          <div className="w-10 h-1 rounded-full bg-gray-300" data-id="element-122" />
        </div>

        {/* Form */}
        <div className="p-1" data-id="element-123">
          <CallbackForm data-id="element-124" />
        </div>
      </div>

      <style data-id="element-125">{`
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slide-up {
          from { opacity: 0; transform: translateY(40px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.2s ease-out;
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </div>;
}